type EnumOrValue<T extends string | number> = T | `${T}`;

export enum ChatMessageStatus {
  VALIDATED = "validated",
  FILTERED = "filtered",
}

export interface IChatMessageStatusUpdate {
  /**
   * Event type discriminator
   */
  type: "status-update";
  chat_message_id: string;
  reply_to_id: string | null;
  event_by_id: string | null;
  status: EnumOrValue<ChatMessageStatus>;
}

export interface IChatMessageAuthor {
  id: string;
  username: string;
  avatar: string | null;
  country_iso: string | null;
  has_paid_membership: boolean;
}

export interface IChatMessagePayload {
  id: string;
  content: string;
  chat_room_id: string;
  is_question: boolean;
  is_pinned: boolean;
  created_at: string;
  created_by_id: string;
  has_been_checked: boolean;
  moderation_status: string | null;
  reply_to_id: string | null;
  reply_to: {
    id: string;
    content: string;
    created_by_id: string;
    has_been_checked: boolean;
    created_by: IChatMessageAuthor;
    chat_message_translations: { content: string; language: string }[];
  } | null;
  created_by: IChatMessageAuthor;
  chat_message_translations: { content: string; language: string }[];
}

/**
 * Extended status update that includes full message data.
 * When `payload` is present, the frontend can render the message
 * directly without a separate GraphQL fetch.
 *
 * `chat_message_translations` in the payload is always `[]` on initial delivery —
 * translations arrive later via the `chat-translation` event type.
 */
export interface IChatMessageStatusUpdateWithPayload extends IChatMessageStatusUpdate {
  payload?: IChatMessagePayload;
}

export interface IChatTranslationEvent {
  /**
   * Event type discriminator — distinguishes translation events
   * from status-update, removed, and pin-updated events on the
   * same chat Ably channel.
   */
  type: "chat-translation";
  /** Schema version for forward compatibility during rolling deploys. */
  version: 1;
  /** ID of the chat message whose content has been translated. */
  chat_message_id: string;
  /** Chat room the message belongs to — used by the frontend to locate the correct room's message list. */
  chat_room_id: string;
  /**
   * Map of language code → translated content string.
   * e.g., { "es": "Hola", "fr": "Bonjour" }
   */
  translations: Record<string, string>;
}

/**
 * Returns the Ably channel name for chat events for a given show or channel context.
 * Single source of truth used by both the API (publisher) and frontend (subscriber).
 */
export function getChatEventsChannelName(
  context:
    | { type: "show"; showId: string }
    | { type: "channel"; channelSlug: string },
): string {
  return context.type === "show"
    ? `fanfest-chat:${context.showId}:chat-events`
    : `fanfest-chat:${context.channelSlug}:chat-events`;
}
